import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { MailCheck, MailX, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying");
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState("error");
      setMessage("This verification link is invalid.");
      return;
    }
    axios
      .post(`${API}/api/auth/verification/confirm`, { token })
      .then(() => setState("success"))
      .catch((e) => {
        setState("error");
        setMessage(e.response?.data?.detail || "This verification link is invalid.");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4" data-testid="verify-email-page">
      <Link to="/" className="mb-8">
        <Logo height={34} />
      </Link>
      <div className="bg-white border border-gray-200 rounded-2xl px-8 py-12 text-center max-w-md w-full">
        {state === "verifying" && (
          <div data-testid="verify-email-loading">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500">Confirming your email…</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="verify-email-success">
            <div className="mx-auto mb-5 h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
              <MailCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">Email verified</h1>
            <p className="text-sm text-gray-500 leading-relaxed">
              Thank you — your email address is confirmed and your Inter-View employer account is secured.
            </p>
            <Link to="/employers/hub">
              <Button className="mt-8 bg-blue-600 hover:bg-blue-700 text-white" data-testid="verify-email-hub-cta">
                Go to your Hub
              </Button>
            </Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-email-error">
            <div className="mx-auto mb-5 h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
              <MailX className="h-6 w-6 text-red-500" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">Verification unsuccessful</h1>
            <p className="text-sm text-gray-500 leading-relaxed" data-testid="verify-email-error-message">{message}</p>
            <p className="mt-4 text-xs text-gray-400">
              You can request a fresh verification email from the banner in your Employer Hub.
            </p>
            <Link to="/login">
              <Button variant="outline" className="mt-8 text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="verify-email-login-cta">
                Sign in
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
